<!-- App.vue -->
<template>
  <v-app>
    <div v-if="isLoading" id="loading">
      <v-img :src="logoImage" alt="로고" max-width="300" contain />
    </div>

    <div v-show="!isLoading">
      <AppBar
        :logo-image="logoImage"
        :user="user"
        :user-info="userInfo"
        :formatted-credit="formattedCredit"
        @signInWithGoogle="signInWithGoogle"
        @signOut="signOut"
        @toggleDrawer="toggleDrawer"
        @openUsageGuide="openUsageGuide"
        @openUserInfoForm="openUserInfoForm"
        @withdrawMembership="withdrawMembership"
        @showNoticeDialog="openCurrentNoticeDialog"
      />

      <v-dialog v-model="showUserInfoForm" persistent max-width="400px" scrollable>
        <v-card>
          <DialogTitleBar>
            <span>{{ isRegistered ? '사용자 정보 변경' : '가입' }}</span>
            <template v-if="isRegistered" #actions>
              <v-btn icon variant="text" aria-label="닫기" @click="closeUserInfoForm">
                <svg-icon type="mdi" :path="icons.mdiClose" />
              </v-btn>
            </template>
          </DialogTitleBar>
          <v-card-text>
            <v-form ref="userInfoForm" @submit.prevent="saveUserInfo">
              <v-text-field
                v-model="nickname"
                label="별명"
                :rules="[(v) => !!v || '별명을 입력해 주세요.']"
                variant="underlined"
                required
              />

              <v-text-field
                v-model="affiliation"
                label="소속(선택 사항)"
                variant="underlined"
              />

              <v-checkbox
                v-model="emailUpdatesConsent"
                base-color="primary"
                color="primary"
                label="업데이트 안내 이메일 수신에 동의합니다."
                class="mt-2"
              />

              <div v-if="!isRegistered">
                <v-checkbox
                  v-model="termsAgreement"
                  base-color="primary"
                  color="primary"
                  class="mt-2"
                  required
                  :rules="[(v) => !!v || '서비스 이용 약관에 동의해야 합니다.']"
                >
                  <template #label>
                    <span>(필수)<a href="/terms" target="_blank" rel="noopener noreferrer">서비스 이용 약관</a>에 동의합니다.</span>
                  </template>
                </v-checkbox>

                <v-checkbox
                  v-model="privacyPolicyAgreement"
                  base-color="primary"
                  color="primary"
                  class="mt-2"
                  required
                  :rules="[(v) => !!v || '개인정보처리방침에 동의해야 합니다.']"
                >
                  <template #label>
                    <span>(필수)<a href="/privacy" target="_blank" rel="noopener noreferrer">개인정보처리방침</a>에 동의합니다.</span>
                  </template>
                </v-checkbox>
              </div>

              <div v-else class="mt-4">
                <span>
                  <a href="/terms" target="_blank" rel="noopener noreferrer">서비스 이용 약관</a> 및
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">개인정보처리방침</a>의 내용을 확인하실 수 있습니다.
                </span>
              </div>

              <v-btn color="primary" type="submit" class="mt-5">{{ isRegistered ? '저장' : '가입' }}</v-btn>
            </v-form>
          </v-card-text>
        </v-card>
      </v-dialog>

      <NoticeDialog v-model="showNoticeDialog" :notice="activeNotice" />
      <UsageGuideDialog v-model="showUsageGuideDialog" />
      <UserWithdrawDialog v-model="showWithdrawDialog" @withdraw="confirmWithdraw" />
      <UserReauthDialog v-model="showReauthenticationDialog" @reauth="proceedWithReauthentication" />
      <DialogServiceHost />
      <CreditDialog
        v-model="showCreditDialog"
        :credit="userCredit"
        :required-credit="requiredCreditForDialog"
      />

      <v-main>
        <router-view v-slot="{ Component }">
          <component
            :is="Component"
            :user="user"
            :user-info="userInfo"
            :is-registered="isRegistered"
            :user-settings="userSettings"
            @signIn="signInWithGoogle"
            @showSnackbar="showSnackbar"
            @openCreditDialog="openCreditDialog"
            @toggleDrawer="toggleDrawer"
            @updateSeedAndSave="updateSeedAndSave"
            @requestRefreshUserInfo="refreshUserInfo"
            @updateSetting="handleImportedSettings"
            @withdrawMembership="withdrawMembership"
          />
        </router-view>
      </v-main>

      <v-dialog
        v-model="showSettings"
        fullscreen
        :persistent="settingsSetupRequired || showStorePromptDialog || showCloseConfirmDialog"
        scrim
        transition="slide-x-reverse-transition"
      >
        <div class="settings-dialog-shell" @click.self="toggleDrawer(false)">
          <v-card class="settings-drawer-card" :style="{ width: drawerWidth }" rounded="0">
            <SettingsForm
              ref="settingsForm"
              :key="userSettings ? userSettings.updatedAt || userSettings.id : 'default'"
              :user-settings="userSettings"
              :setup-required="settingsSetupRequired"
              :user-id="user ? user.uid : ''"
              @submitSettings="onDrawerSave"
              @openStorePromptDialog="toggleStorePromptDialog"
              @close="toggleDrawer(false)"
              @settings-changed="isSettingsDirty = true"
            />
            <StorePromptDialog
              v-model="showStorePromptDialog"
              :user-id="user ? user.uid : ''"
              :settings="userSettings"
              @showSnackbar="showSnackbar"
              @preventDrawerClose="preventDrawerClose"
            />
          </v-card>
        </div>
      </v-dialog>

      <v-dialog v-model="showCloseConfirmDialog" persistent max-width="400px">
        <v-card rounded="0">
          <v-toolbar color="primary" density="compact">
            <v-toolbar-title class="subtitle-1">설정 변경 알림</v-toolbar-title>
            <v-spacer />
          </v-toolbar>

          <v-card-text class="mt-4">
            설정이 변경되었습니다. 저장하시겠습니까?
          </v-card-text>

          <v-divider />

          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" variant="text" @click="confirmSave">저장</v-btn>
            <v-btn variant="text" @click="confirmDiscard">변경 취소</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-footer v-if="!isASGS" class="main-footer">
        <div class="footer-shell">
          <div class="footer-grid">
            <section class="footer-brand" aria-label="AI세특 소개">
              <div class="footer-brand-row">
                <img :src="logoImage" alt="AI세특 로고" />
                <strong>AI세특</strong>
              </div>
              <p>선생님의 업무 시간을 줄여주는<br />스마트한 교육 파트너</p>
            </section>

            <nav class="footer-column" aria-label="서비스">
              <h2>서비스</h2>
              <a href="/#features">기능 소개</a>
              <a href="/#pricing">크레딧 안내</a>
              <a href="https://open.kakao.com/o/sTf1el7g" target="_blank" rel="noopener noreferrer">학교 도입 문의</a>
            </nav>

            <nav class="footer-column" aria-label="교사용 도구">
              <h2>교사용 도구</h2>
              <a href="/redact-pdf/">PDF 개인정보 삭제</a>
              <a href="/pdf-manager/">PDF 매니저</a>
              <a href="/seat-planner/">교실 자리배치</a>
              <a href="/highlearning-score-save/">하이러닝 점수 자동저장</a>
            </nav>

            <nav class="footer-column" aria-label="고객 지원">
              <h2>고객 지원</h2>
              <a href="/faq">자주 묻는 질문</a>
              <a href="/guide/2026">학생부 기재요령</a>
              <a href="mailto:papermbl@gmail.com?subject=AI%EC%84%B8%ED%8A%B9%20%EB%AC%B8%EC%9D%98">문의하기</a>
            </nav>

            <nav class="footer-column" aria-label="법적 고지">
              <h2>법적 고지</h2>
              <a href="/terms">이용약관</a>
              <a href="/privacy">개인정보처리방침</a>
              <button type="button" @click="openUsageGuide">이용안내</button>
            </nav>
          </div>

          <div class="footer-bottom">
            <p class="footer-copyright">Copyright © 인성유통. All Rights Reserved.</p>
            <div class="footer-company-wrap">
              <a class="footer-payment" href="/#pricing" aria-label="크레딧 안내">
                <img src="/img/kakaopay_signage.png" alt="카카오페이 사이니지" />
              </a>
              <div class="footer-company">
                <p>상호: 인성유통 | 대표자: 신인성 | 사업자등록번호: 124-46-90107</p>
                <p>주소: 16603 경기도 수원시 권선구 탑동로29번길 36 301호</p>
                <p>
                  통신판매업 신고번호: 2012-수원권선-0179
                  <a href="https://www.ftc.go.kr/bizCommPop.do?wrkr_no=1244690107" target="_blank" rel="noopener noreferrer">[사업자정보확인]</a>
                </p>
                <p>개인정보보호책임자: 신인성</p>
              </div>
            </div>
          </div>
        </div>
      </v-footer>

      <v-snackbar
        v-model="snackbar.show"
        :timeout="snackbar.timeout || 1500"
        :color="snackbar.color || 'info'"
        location="top end"
        class="custom-snackbar"
      >
        {{ snackbar.message }}
        <template #actions>
          <v-btn color="pink" variant="text" @click="snackbar.show = false">닫기</v-btn>
        </template>
      </v-snackbar>

      <!-- Custom Ybug Launcher -->
      <div class="custom-ybug-launcher" @click="openBugReportDialog">
        <svg-icon type="mdi" :path="icons.mdiCommentSmile" :size="20" class="launcher-icon" />
        <span class="launcher-text">문의와 개선 요청</span>
      </div>
    </div>
  </v-app>
</template>

<script>
import logoImage from '@/assets/AI_logo_150x150_adjusted.png';
import { auth, db } from '@/firebase';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  reauthenticateWithPopup,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  collection,
  deleteDoc,
  setDoc,
  serverTimestamp,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { saveUserSettings } from '@/utils/firebaseUtils';
import { completeUserRegistration } from '@/utils/accountApi';
import { isKakaoNaverInAppBrowser } from '@/utils/browserUtils';
import {
  buildUserInfoState,
  cloneValue,
  createSignedOutState,
  createUserFormState,
  shouldShowNoticeDialog,
} from '@/utils/appHelpers';
import {
  getNoticeSuppressionKey,
  normalizeNotice,
  selectCurrentNotice,
} from '@/utils/noticeHelpers';
import { ADMIN_EMAILS } from '@/router';
import AppBar from '@/components/AppBar.vue';
import DialogTitleBar from '@/components/DialogTitleBar.vue';
import SettingsForm from '@/components/SettingsForm.vue';
import CreditDialog from '@/components/CreditDialog.vue';
import StorePromptDialog from '@/components/StorePromptDialog.vue';
import NoticeDialog from '@/components/NoticeDialog.vue';
import UsageGuideDialog from '@/components/UsageGuideDialog.vue';
import UserWithdrawDialog from '@/components/UserWithdrawDialog.vue';
import UserReauthDialog from '@/components/UserReauthDialog.vue';
import DialogServiceHost from '@/components/DialogServiceHost.vue';
import icons from '@/icons';
import responsiveMixin from '@/mixins/responsiveMixin';
import { isCompleteUserSettings } from '@/utils/specialNoteHelpers';

export default {
  components: {
    AppBar,
    DialogTitleBar,
    SettingsForm,
    CreditDialog,
    StorePromptDialog,
    NoticeDialog,
    UsageGuideDialog,
    UserWithdrawDialog,
    UserReauthDialog,
    DialogServiceHost,
  },
  mixins: [responsiveMixin],
  data() {
    return {
      isLoading: true,
      isInApp: false,
      logoImage,
      icons,
      user: null,
      isRegistered: false,
      isProcessingWithdrawal: false,
      showUserInfoForm: false,
      userInfo: null,
      nickname: '',
      affiliation: '',
      emailUpdatesConsent: false,
      termsAgreement: false,
      privacyPolicyAgreement: false,
      showUsageGuideDialog: false,
      showSettings: false,
      isSettingsDirty: false,
      originalSettingsSnapshot: null,
      showCloseConfirmDialog: false,
      showWithdrawDialog: false,
      showReauthenticationDialog: false,
      showCreditDialog: false,
      requiredCreditForDialog: 0,
      showNoticeDialog: false,
      activeNotice: null,
      showStorePromptDialog: false,
      snackbar: {
        show: false,
        message: '',
      },
      userSettings: {},
      preventDrawerCloseFlag: false,
      storedPrompts: [],
      autoSignInAttempted: false,
      pendingEntryIntent: null,
      ybugWatcherInterval: null,
      isNavigatingToStaticLanding: false,
    };
  },
  watch: {
    user(newUser) {
      if (!newUser) {
        this.userInfo = null;
        this.storedPrompts = [];
      }
    },
    showUserInfoForm(val) {
      if (!this.isProcessingWithdrawal && !val && !this.isRegistered && this.user) {
        this.$nextTick(() => {
          if (this.user) {
            this.showUserInfoForm = true;
          }
        });
      }
    },
    showSettings(val) {
      if (val) {
        this.originalSettingsSnapshot = cloneValue(this.userSettings);
        this.isSettingsDirty = false;
      } else if (this.settingsSetupRequired) {
        this.$nextTick(() => {
          this.showSettings = true;
        });
      } else if (this.isSettingsDirty) {
        this.showSettings = true;
        this.showCloseConfirmDialog = true;
      }
    },
    '$route.query.notice': {
      immediate: true,
      handler(value) {
        this.handleRouteNotice(value);
      },
    },
    '$route.query.auth': {
      handler() {
        this.maybeStartQuerySignIn();
      },
    },
  },
  computed: {
    settingsComplete() {
      return isCompleteUserSettings(this.userSettings);
    },
    settingsSetupRequired() {
      return Boolean(this.user && this.isRegistered && !this.settingsComplete);
    },
    drawerWidth() {
      if (this.isXs) return '100%';
      if (this.isSm) return '80%';
      if (this.isMd) return '60%';
      return '50%';
    },
    userCredit() {
      return this.userInfo?.credit ?? 0;
    },
    formattedCredit() {
      return this.userInfo?.credit?.toLocaleString() ?? '0';
    },
  },
  async mounted() {
    onAuthStateChanged(auth, async (user) => {
      await this.handleAuthStateChange(user);
    });

    history.replaceState({ page: 'main' }, null, null);
    window.addEventListener('popstate', this.handlePopState);

    this.isInApp = isKakaoNaverInAppBrowser();
    await this.loadCurrentNotice();
    const dontShowUntil = this.activeNotice?.id
      ? localStorage.getItem(getNoticeSuppressionKey(this.activeNotice.id))
      : null;
    const shouldOpenNoticeDialog = shouldShowNoticeDialog({
      isInApp: this.isInApp,
      dontShowUntil,
    });
    this.showNoticeDialog = Boolean(this.activeNotice) && !this.$route.query.notice && shouldOpenNoticeDialog;

    setTimeout(() => {
      this.isLoading = false;
    }, 2000);
    this.watchAndApplyYbugCustomizations();
  },
  beforeUnmount() {
    window.removeEventListener('popstate', this.handlePopState);
    if (this.ybugWatcherInterval) {
      clearInterval(this.ybugWatcherInterval);
    }
  },
  methods: {
    async loadCurrentNotice() {
      try {
        const noticesQuery = query(collection(db, 'notices'), where('isPublished', '==', true));
        const noticeSnapshot = await getDocs(noticesQuery);
        const notices = noticeSnapshot.docs.map((noticeDoc) => normalizeNotice(noticeDoc));
        this.activeNotice = selectCurrentNotice(notices);
      } catch (error) {
        console.error('공지사항을 가져오는 데 실패했습니다:', error);
        this.activeNotice = null;
      }
    },
    openCurrentNoticeDialog() {
      if (!this.activeNotice) {
        this.showSnackbar('현재 표시할 공지사항이 없습니다.', 'info', 2500);
        return;
      }

      this.showNoticeDialog = true;
    },
    isAdminRoute() {
      return this.$route.matched.some((record) => record.meta.requiresAdmin);
    },
    redirectHomeFromAdminDenied() {
      if (!this.isAdminRoute()) {
        return;
      }

      this.showNoticeDialog = false;
        this.$router.replace({
          name: 'Home',
        query: {
          ...this.$route.query,
          notice: 'admin-denied',
        },
      }).catch(() => {});
    },
    isAppHomeRoute() {
      return this.$route.name === 'Home';
    },
    navigateToStaticLanding({ notice = null } = {}) {
      if (this.isNavigatingToStaticLanding) {
        return;
      }

      this.isNavigatingToStaticLanding = true;
      const query = notice ? `?notice=${encodeURIComponent(notice)}` : '';
      window.location.assign(`/${query}`);
    },
    handleRouteNotice(value) {
      if (value !== 'admin-denied') {
        return;
      }

      if (!this.user && !auth.currentUser && this.isAppHomeRoute()) {
        return;
      }

      this.showNoticeDialog = false;
      this.showSnackbar('접근 권한이 없습니다.', 'error', 3000);
      const nextQuery = { ...this.$route.query };
      delete nextQuery.notice;
      this.$router.replace({
        path: this.$route.path,
        query: nextQuery,
      }).catch(() => {});
    },
    async handleAuthStateChange(user) {
      if (user) {
        const deletedUserDoc = await getDoc(doc(db, 'deletedUsers', user.email));
        if (deletedUserDoc.exists()) {
          const data = deletedUserDoc.data();
          const deletedAt = data.deletedAt.toDate();
          const oneWeekLater = new Date(deletedAt.getTime() + 7 * 24 * 60 * 60 * 1000);

          if (new Date() < oneWeekLater) {
            this.showSnackbar('탈퇴 후 7일간 재가입이 허용되지 않습니다.');
            await firebaseSignOut(auth);
            return;
          }

          await deleteDoc(doc(db, 'deletedUsers', user.email));
        }

        this.user = user;
        if (this.isAdminRoute() && !ADMIN_EMAILS.includes(user.email)) {
          this.redirectHomeFromAdminDenied();
          return;
        }
        await this.syncUserStateFromFirestore();
        this.consumeEntryQuery();
        return;
      }

      this.user = null;
      this.applySignedOutUiState();
      if (this.isAdminRoute()) {
        this.navigateToStaticLanding({ notice: 'admin-denied' });
        return;
      }

      if (this.isAppHomeRoute() && this.$route.query.auth !== 'google') {
        this.navigateToStaticLanding({
          notice: this.$route.query.notice === 'admin-denied' ? 'admin-denied' : null,
        });
        return;
      }

      this.maybeStartQuerySignIn();
    },
    maybeStartQuerySignIn() {
      if (this.autoSignInAttempted || this.user || this.$route.query.auth !== 'google') {
        return;
      }

      this.autoSignInAttempted = true;
      this.signInWithGoogle();
    },
    hasEntryQuery(query = this.$route.query) {
      return ['auth', 'next', 'welcome'].some((key) => Object.prototype.hasOwnProperty.call(query, key));
    },
    getQueryStringValue(value) {
      if (Array.isArray(value)) {
        return value.find((item) => typeof item === 'string') || '';
      }

      return typeof value === 'string' ? value : '';
    },
    normalizeEntryIntent(query = this.$route.query) {
      const next = this.getQueryStringValue(query.next).toLowerCase();
      if (!['pay', 'payment', 'settings', 'account'].includes(next)) {
        return null;
      }

      const charge = this.getQueryStringValue(query.charge).trim();
      return {
        next,
        charge: charge || null,
      };
    },
    buildEntryQuery({ preserveIntent = false, intent = null } = {}) {
      const query = { ...this.$route.query };
      const preservedIntent = preserveIntent ? intent || this.normalizeEntryIntent(query) : null;

      delete query.auth;
      delete query.welcome;

      if (!preservedIntent) {
        delete query.next;
        delete query.charge;
        return query;
      }

      query.next = preservedIntent.next;
      if (
        preservedIntent.charge &&
        (preservedIntent.next === 'pay' || preservedIntent.next === 'payment')
      ) {
        query.charge = preservedIntent.charge;
      } else {
        delete query.charge;
      }

      return query;
    },
    captureEntryIntent(query = this.$route.query) {
      const intent = this.normalizeEntryIntent(query);
      if (intent) {
        this.pendingEntryIntent = intent;
      } else {
        this.pendingEntryIntent = null;
      }

      return intent;
    },
    applyEntryIntent(intent = this.pendingEntryIntent) {
      if (!intent) {
        return false;
      }
      if (this.settingsSetupRequired) {
        this.pendingEntryIntent = intent;
        this.showSettings = true;
        return false;
      }

      this.pendingEntryIntent = null;

      if (intent.next === 'pay' || intent.next === 'payment') {
        const query = intent.charge ? { charge: intent.charge } : {};
        this.$router.replace({ name: 'Payment', query }).catch(() => {});
        return true;
      }

      if (intent.next === 'settings') {
        this.$router.replace({ name: 'Home', query: this.buildEntryQuery() }).catch(() => {});
        this.$nextTick(() => {
          this.toggleDrawer(true);
        });
        return true;
      }
      if (intent.next === 'account') {
        this.$router.replace({ name: 'Account' }).catch(() => {});
        return true;
      }

      return false;
    },
    consumeEntryQuery() {
      if (!this.hasEntryQuery()) {
        return;
      }

      const intent = this.captureEntryIntent();
      if (!this.isRegistered || !this.settingsComplete) {
        this.$router.replace({
          name: 'Home',
          query: this.buildEntryQuery({ preserveIntent: Boolean(intent), intent }),
        }).catch(() => {});
        if (this.isRegistered) {
          this.showSettings = true;
        }
        return;
      }

      if (this.applyEntryIntent(intent)) {
        return;
      }

      this.$router.replace({ name: 'Home', query: this.buildEntryQuery() }).catch(() => {});
    },
    applySignedOutUiState() {
      this.pendingEntryIntent = null;
      this.isSettingsDirty = false;
      this.showStorePromptDialog = false;
      this.showCloseConfirmDialog = false;
      this.originalSettingsSnapshot = null;
      this.showSettings = false;
      Object.assign(this, createSignedOutState());
      this.resetYbugAnonymous();
    },
    async syncUserStateFromFirestore() {
      if (!this.user?.uid) {
        this.isRegistered = false;
        this.showUserInfoForm = false;
        return;
      }

      try {
        const userRef = doc(db, 'users', this.user.uid);
        const userDocSnapshot = await getDoc(userRef);

        if (userDocSnapshot.exists()) {
          const data = userDocSnapshot.data();
          this.userInfo = buildUserInfoState(this.user, data);
          this.userSettings = data.settings || {};

          if (!data.termsAgreement || !data.privacyPolicyAgreement) {
            this.isRegistered = false;
            if (!this.isProcessingWithdrawal) {
              this.openUserInfoForm();
            }
          } else {
            this.isRegistered = true;
            this.showUserInfoForm = false;
            if (!isCompleteUserSettings(this.userSettings)) {
              this.$nextTick(() => {
                this.showSettings = true;
              });
            }
          }
        } else {
          this.userInfo = null;
          this.userSettings = {};
          this.isRegistered = false;
          if (!this.isProcessingWithdrawal) {
            this.openUserInfoForm();
          }
        }
      } catch (_error) {
        this.isRegistered = false;
        if (!this.isProcessingWithdrawal) {
          this.openUserInfoForm();
        }
      }

      await this.fetchStoredPrompts();
      this.identifyYbugUser();
    },
    async onDrawerSave(settings) {
      const saved = await this.submitSettings(settings);
      if (saved) {
        this.isSettingsDirty = false;
      }
    },
    async confirmSave() {
      const edited = cloneValue(this.$refs.settingsForm.settings);
      await this.onDrawerSave(edited);
      this.showCloseConfirmDialog = false;
    },
    confirmDiscard() {
      if (this.settingsSetupRequired) {
        this.showSnackbar('최초 시스템 설정을 저장해야 합니다.', 'warning');
        return;
      }
      this.userSettings = cloneValue(this.originalSettingsSnapshot);
      this.isSettingsDirty = false;
      this.showSettings = false;
      this.showCloseConfirmDialog = false;
    },
    async handleImportedSettings({ key, value }) {
      if (!this.user) {
        this.showSnackbar('설정 저장 권한이 없습니다.', 'error');
        return;
      }

      const newSettings = { ...this.userSettings, [key]: value };
      try {
        await this.submitSettings(newSettings);
        this.showSnackbar('시스템 설정이 업데이트되었습니다.', 'success');
      } catch (error) {
        console.error(error);
        this.showSnackbar('시스템 설정 저장에 실패했습니다.', 'error');
      }
    },
    async signInWithGoogle() {
      const provider = new GoogleAuthProvider();
      try {
        const loopbackHosts = new Set(['localhost', '127.0.0.1', '::1']);
        const isLocalHost = loopbackHosts.has(window.location.hostname);
        if (isLocalHost) {
          await signInWithPopup(auth, provider);
        } else {
          await signInWithRedirect(auth, provider);
        }
        this.showSnackbar('로그인에 성공했습니다.');
      } catch (_error) {
        this.showSnackbar('로그인에 실패했습니다.');
        this.navigateToStaticLanding();
      }
    },
    async signOut() {
      try {
        const notice = this.isAdminRoute() ? 'admin-denied' : null;
        await firebaseSignOut(auth);
        this.applySignedOutUiState();
        this.navigateToStaticLanding({ notice });
      } catch (_error) {
        this.showSnackbar('로그아웃에 실패했습니다.');
      }
    },
    openUserInfoForm() {
      if (this.isProcessingWithdrawal) {
        return;
      }

      if (this.isRegistered) {
        this.$router.push({ name: 'Account' }).catch(() => {});
        return;
      }

      Object.assign(
        this,
        createUserFormState({
          isRegistered: this.isRegistered,
          userInfo: this.userInfo,
        })
      );
      this.showUserInfoForm = true;
      history.pushState({ modalType: 'userInfo' }, null, null);
    },
    closeUserInfoForm() {
      if (this.isRegistered) {
        this.showUserInfoForm = false;
      } else {
        this.showSnackbar('가입을 완료해야 합니다.');
      }
    },
    async saveUserInfo() {
      if (!this.nickname) {
        this.showSnackbar('필수 항목을 모두 입력해주세요.');
        return;
      }
      if (!this.user) {
        this.showSnackbar('로그인되지 않은 상태입니다.');
        return;
      }
      if (!this.isRegistered && (!this.termsAgreement || !this.privacyPolicyAgreement)) {
        this.showSnackbar('약관/개인정보처리방침 동의가 필요합니다.');
        return;
      }

      try {
        const wasRegistered = this.isRegistered;
        await completeUserRegistration({
          nickname: this.nickname,
          affiliation: this.affiliation,
          emailUpdatesConsent: this.emailUpdatesConsent,
          termsAgreement: this.termsAgreement,
          privacyPolicyAgreement: this.privacyPolicyAgreement,
        });
        this.isRegistered = true;
        this.showUserInfoForm = false;
        this.showSnackbar('사용자 정보가 저장되었습니다.');
        await this.refreshUserInfo();
        if (!wasRegistered) {
          if (this.settingsComplete) {
            this.applyEntryIntent(this.pendingEntryIntent || this.normalizeEntryIntent());
          } else {
            this.showSettings = true;
          }
        }
      } catch (_error) {
        this.showSnackbar('사용자 정보 저장에 실패했습니다.');
      }
    },
    toggleDrawer(forceValue = null) {
      const nextValue = typeof forceValue === 'boolean' ? forceValue : !this.showSettings;
      if (!nextValue && this.settingsSetupRequired) {
        this.showSnackbar('최초 시스템 설정을 저장해야 합니다.', 'warning');
        return;
      }
      if (!nextValue && (this.showStorePromptDialog || this.showCloseConfirmDialog)) {
        return;
      }
      this.showSettings = nextValue;
    },
    preventDrawerClose() {
      this.preventDrawerCloseFlag = true;
      setTimeout(() => {
        this.preventDrawerCloseFlag = false;
      }, 100);
    },
    handlePopState(_event) {
      if (this.preventDrawerCloseFlag) return;
      if (this.showStorePromptDialog) {
        this.showStorePromptDialog = false;
      } else if (this.showCloseConfirmDialog) {
        this.showCloseConfirmDialog = false;
      } else if (this.showUserInfoForm && !this.isRegistered) {
        this.closeUserInfoForm();
        this.signOut();
      } else if (this.showSettings) {
        this.toggleDrawer(false);
      } else if (this.showUsageGuideDialog) {
        this.showUsageGuideDialog = false;
      }
    },
    updateSeedAndSave(seedVal) {
      this.userSettings.openAIParameters.seed = seedVal;
      this.userSettings.openAIParameters.randomSeed = false;
      this.submitSettings(this.userSettings);
    },
    async submitSettings(settings) {
      if (!this.user) {
        this.showSnackbar('사용자 정보가 없습니다.', 'error');
        return false;
      }
      if (!isCompleteUserSettings(settings)) {
        this.showSnackbar('필수 시스템 설정을 모두 입력해 주세요.', 'warning');
        this.showSettings = true;
        return false;
      }

      try {
        await saveUserSettings(this.user.uid, settings);
        this.userSettings = settings;
        this.showSnackbar('설정이 저장되었습니다.', 'success');
        this.showSettings = false;
        await this.refreshUserInfo();
        this.applyEntryIntent(this.pendingEntryIntent);
        return true;
      } catch (_error) {
        this.showSnackbar('설정 저장에 실패했습니다.', 'error');
        return false;
      }
    },
    openCreditDialog(payload = {}) {
      if (this.isASGS) {
        this.showSnackbar('안산강서고 선생님은 결제 기능을 사용할 수 없습니다.');
        return;
      }
      this.requiredCreditForDialog = Number(payload.requiredCredit || 0);
      this.showCreditDialog = true;
    },
    openBugReportDialog() {
      if (this.user) {
        const name = this.userInfo?.nickname || this.user.displayName || '사용자';
        const email = this.userInfo?.email || this.user.email || '';
        if (window.Ybug && typeof window.Ybug.setUser === 'function') {
          window.Ybug.setUser({ id: this.user.uid, name, email });
        } else if (window.ybug_obj && typeof window.ybug_obj.setUser === 'function') {
          window.ybug_obj.setUser({ id: this.user.uid, name, email });
        } else if (window.ybug && typeof window.ybug.setUser === 'function') {
          window.ybug.setUser({ id: this.user.uid, name, email });
        }
      }

      if (window.Ybug && typeof window.Ybug.open === 'function') {
        window.Ybug.open();
      } else if (window.ybug_obj && typeof window.ybug_obj.open === 'function') {
        window.ybug_obj.open();
      } else if (window.ybug && typeof window.ybug.open === 'function') {
        window.ybug.open();
      } else {
        this.showSnackbar('피드백 위젯을 불러오는 중입니다. 잠시 후 다시 시도해 주세요.');
        return;
      }

      this.watchAndApplyYbugCustomizations();
    },

    withdrawMembership() {
      this.showWithdrawDialog = true;
      history.pushState({ modalType: 'withdraw' }, null, null);
    },
    confirmWithdraw() {
      this.showWithdrawDialog = false;
      this.showReauthenticationDialog = true;
      history.pushState({ modalType: 'reauth' }, null, null);
    },
    async proceedWithReauthentication() {
      this.isProcessingWithdrawal = true;
      this.showReauthenticationDialog = false;
      try {
        const provider = new GoogleAuthProvider();
        await reauthenticateWithPopup(this.user, provider);

        await setDoc(doc(db, 'deletedUsers', this.user.email), {
          email: this.user.email,
          deletedAt: serverTimestamp(),
        });

        const userStoredPromptsRef = collection(db, 'users', this.user.uid, 'storedPrompts');
        const storedPromptsSnapshot = await getDocs(userStoredPromptsRef);
        const deletePromises = storedPromptsSnapshot.docs.map((promptDoc) => deleteDoc(promptDoc.ref));
        await Promise.all(deletePromises);

        await deleteDoc(doc(db, 'users', this.user.uid));
        await this.user.delete();
        await firebaseSignOut(auth);

        this.user = null;
        this.applySignedOutUiState();
        this.showSnackbar('회원 탈퇴가 완료되었습니다.');
      } catch (error) {
        if (error.code === 'auth/popup-closed-by-user') {
          this.showSnackbar('재인증이 취소되었습니다.');
        } else if (error.code === 'auth/user-mismatch') {
          this.showSnackbar('동일한 계정으로 재인증해 주세요.');
        } else if (error.code === 'auth/requires-recent-login') {
          this.showSnackbar('보안을 위해 다시 로그인해야 합니다.');
        } else {
          this.showSnackbar('회원 탈퇴에 실패했습니다. 다시 시도해 주세요.');
        }
      } finally {
        this.isProcessingWithdrawal = false;
      }
    },
    async refreshUserInfo() {
      if (!this.user) {
        return;
      }

      await this.syncUserStateFromFirestore();
    },
    async fetchStoredPrompts() {
      if (!this.user) {
        return;
      }

      try {
        const userStoredPromptsRef = collection(db, 'users', this.user.uid, 'storedPrompts');
        const storedPromptsSnapshot = await getDocs(userStoredPromptsRef);
        this.storedPrompts = storedPromptsSnapshot.docs.map((promptDoc) => ({
          id: promptDoc.id,
          ...promptDoc.data(),
        }));
      } catch (_error) {
        this.showSnackbar('저장된 설정을 불러오는 데 실패했습니다.', 'error');
      }
    },
    identifyYbugUser() {
      if (this.user) {
        const name = this.userInfo?.nickname || this.user.displayName || '사용자';
        const email = this.userInfo?.email || this.user.email || '';
        
        try {
          localStorage.setItem('feedback.email', email);
          localStorage.setItem('feedback.name', name);
        } catch (e) {
          console.error('Ybug localStorage 저장 실패:', e);
        }

        window.ybug_settings = window.ybug_settings || {};
        window.ybug_settings.user = {
          id: this.user.uid,
          name,
          email,
        };
        window.ybug_settings.feedback = window.ybug_settings.feedback || {};
        window.ybug_settings.feedback.email = email;
        window.ybug_settings.feedback.name = name;
        if (window.Ybug && typeof window.Ybug.setUser === 'function') {
          window.Ybug.setUser({ id: this.user.uid, name, email });
        } else if (window.ybug_obj && typeof window.ybug_obj.setUser === 'function') {
          window.ybug_obj.setUser({ id: this.user.uid, name, email });
        } else if (window.ybug && typeof window.ybug.setUser === 'function') {
          window.ybug.setUser({ id: this.user.uid, name, email });
        }
      }
    },
    resetYbugAnonymous() {
      try {
        localStorage.removeItem('feedback.email');
        localStorage.removeItem('feedback.name');
      } catch (e) {
        console.error('Ybug localStorage 삭제 실패:', e);
      }

      if (window.ybug_settings) {
        delete window.ybug_settings.user;
        if (window.ybug_settings.feedback) {
          delete window.ybug_settings.feedback.email;
          delete window.ybug_settings.feedback.name;
        }
      }
      if (window.Ybug && typeof window.Ybug.setUser === 'function') {
        window.Ybug.setUser(null);
      } else if (window.ybug_obj && typeof window.ybug_obj.setUser === 'function') {
        window.ybug_obj.setUser(null);
      } else if (window.ybug && typeof window.ybug.setUser === 'function') {
        window.ybug.setUser(null);
      }
    },
    watchAndApplyYbugCustomizations() {
      if (this.ybugWatcherInterval) {
        clearInterval(this.ybugWatcherInterval);
      }
      this.ybugWatcherInterval = setInterval(() => {
        const iframe = document.getElementById('__ybug-app');
        if (!iframe) {
          return;
        }

        // Skip if the iframe is hidden (closed or screenshot mode)
        const display = window.getComputedStyle(iframe).display;
        if (display === 'none') {
          return;
        }

        const doc = iframe.contentDocument || iframe.contentWindow.document;
        if (!doc) return;

        const heading = doc.querySelector('.page__heading');
        const commentInput = doc.getElementById('comment') || doc.querySelector('textarea');
        const submitBtn = doc.querySelector('button[type="submit"]');
        const emailInput = doc.getElementById('email') || doc.querySelector('input[type="email"]');

        if (heading) {
          const styleId = 'custom-ybug-injected-style';
          
          // 1. Inject custom CSS if it's missing
          if (!doc.getElementById(styleId)) {
            const styleEl = doc.createElement('style');
            styleEl.id = styleId;
            styleEl.textContent = `
              .page__heading {
                font-weight: 500 !important;
                font-size: 1.25rem !important;
              }
              .mb-3 {
                display: flex !important;
                flex-direction: row !important;
                justify-content: space-around !important;
                gap: 12px !important;
                margin-bottom: 16px !important;
                flex-wrap: wrap !important;
              }
              .mb-3 > div.flex {
                margin-bottom: 0 !important;
                flex: 1 !important;
                display: flex !important;
                justify-content: center !important;
                align-items: center !important;
              }
              .mb-3 > div:not(.flex) {
                width: 100% !important;
              }
              .annotate-link {
                font-size: 0 !important;
                color: transparent !important;
                padding: 8px 4px !important;
                border: 1px solid #e2e8f0 !important;
                border-radius: 8px !important;
                width: 100% !important;
                justify-content: center !important;
                transition: background-color 0.2s !important;
              }
              .annotate-link:hover {
                background-color: #f8fafc !important;
              }
              .annotate-link svg {
                margin-right: 0 !important;
                color: #3b82f6 !important;
              }
            `;
            doc.head.appendChild(styleEl);
          }

          // Check if we are on the Thank You success screen
          const thankYouText = doc.querySelector('.page__text');
          if (thankYouText) {
            // Translate Success Heading
            if (heading.innerText !== '감사합니다!') {
              heading.innerText = '감사합니다!';
            }
            // Translate Success Text
            if (thankYouText.innerText !== '소중한 제보가 정상 접수되었습니다.') {
              thankYouText.innerText = '소중한 제보가 정상 접수되었습니다.';
            }
            // Translate Success Close Button
            const closeBtn = doc.querySelector('.button--grow');
            if (closeBtn && !closeBtn.innerText.includes('닫기')) {
              const countdownSpan = closeBtn.querySelector('span');
              if (countdownSpan) {
                closeBtn.innerHTML = `닫기 ${countdownSpan.outerHTML}`;
              } else {
                closeBtn.innerHTML = '닫기';
              }
            }
          } else if (commentInput && submitBtn) {
            // We are on the main Feedback Form screen

            // 2. Translate Heading
            if (heading.innerText !== '😊 안녕하세요, 무엇을 도와드릴까요?') {
              heading.innerText = '😊 안녕하세요, 무엇을 도와드릴까요?';
            }

            // 3. Translate Comment Placeholder
            if (commentInput.placeholder !== '의견이나 개선 요청 사항을 입력해주세요.') {
              commentInput.placeholder = '의견이나 개선 요청 사항을 입력해주세요.';
            }

            // 4. Translate Submit Button Text
            if (!submitBtn.innerText.includes('보내기')) {
              submitBtn.innerHTML = '보내기';
            }

            // 5. Prefill Email Input Field & Translate Placeholder
            if (emailInput) {
              const setIframeInputValue = (inputEl, val) => {
                try {
                  const iframeWindow = iframe.contentWindow;
                  const valueSetter = iframeWindow.Object.getOwnPropertyDescriptor(iframeWindow.HTMLInputElement.prototype, 'value')?.set;
                  const prototype = iframeWindow.Object.getPrototypeOf(inputEl);
                  const prototypeValueSetter = iframeWindow.Object.getOwnPropertyDescriptor(prototype, 'value')?.set;

                  if (prototypeValueSetter && valueSetter !== prototypeValueSetter) {
                    prototypeValueSetter.call(inputEl, val);
                  } else if (valueSetter) {
                    valueSetter.call(inputEl, val);
                  } else {
                    inputEl.value = val;
                  }
                } catch (err) {
                  console.warn('Native value setter failed:', err);
                  inputEl.value = val;
                }
                inputEl.dispatchEvent(new Event('input', { bubbles: true }));
                inputEl.dispatchEvent(new Event('change', { bubbles: true }));
                inputEl.dispatchEvent(new Event('blur', { bubbles: true }));
              };

              if (emailInput.placeholder !== '이메일 주소') {
                emailInput.placeholder = '이메일 주소';
              }
              if (this.user) {
                const email = this.userInfo?.email || this.user.email || '';
                if (emailInput.value !== email) {
                  setIframeInputValue(emailInput, email);
                  try {
                    localStorage.setItem('feedback.email', email);
                  } catch (e) {
                    console.warn('Ybug email sync failed:', e);
                  }
                }
              } else {
                if (emailInput.value !== '') {
                  setIframeInputValue(emailInput, '');
                  try {
                    localStorage.removeItem('feedback.email');
                  } catch (e) {
                    console.warn('Ybug email clear failed:', e);
                  }
                }
              }
            }

            // 6. Translate Custom Dropdown Field "Feedback type" and its options if present
            const selects = doc.querySelectorAll('select');
            selects.forEach(select => {
              const options = select.querySelectorAll('option');
              options.forEach(opt => {
                const txt = opt.innerText.trim().toLowerCase();
                const val = opt.value.trim().toLowerCase();
                if (txt === 'bug' || val === 'bug') {
                  opt.innerText = '버그 신고';
                } else if (txt === 'improvement' || val === 'improvement') {
                  opt.innerText = '기능 추가 요청';
                } else if (txt === 'question' || val === 'question') {
                  opt.innerText = '불편한 점';
                } else if (txt === 'feedback' || val === 'feedback') {
                  opt.innerText = '기타 문의';
                } else if (txt === 'feedback type' || val === 'feedback type' || txt.includes('feedback type') || txt.includes('feedback_type')) {
                  opt.innerText = '선택';
                }
              });
            });

            const labels = doc.querySelectorAll('label');
            labels.forEach(label => {
              const txt = label.innerText.trim().toLowerCase();
              if (txt === 'feedback type' || txt === 'feedback_type' || txt.includes('feedback type') || txt.includes('feedback_type')) {
                label.innerText = '선택';
              }
            });

            // Handle custom divs/spans just in case Ybug uses a custom dropdown style
            const textElements = doc.querySelectorAll('span, div, li, a, button, p');
            textElements.forEach(el => {
              if (el.children.length === 0) {
                const txt = el.innerText.trim().toLowerCase();
                if (txt === 'bug') {
                  el.innerText = '버그 신고';
                } else if (txt === 'improvement') {
                  el.innerText = '기능 추가 요청';
                } else if (txt === 'question') {
                  el.innerText = '불편한 점';
                } else if (txt === 'feedback') {
                  el.innerText = '기타 문의';
                } else if (txt === 'feedback type' || txt === 'feedback_type') {
                  el.innerText = '선택';
                }
              }
            });
          }
        }
      }, 250);
    },
    showSnackbar(message, color = 'info', timeout = 1500) {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.timeout = timeout;
      this.snackbar.show = true;
    },
    openUsageGuide() {
      this.showUsageGuideDialog = true;
    },
    toggleStorePromptDialog() {
      this.showStorePromptDialog = !this.showStorePromptDialog;
    },
  },
};
</script>

<style scoped>
.v-toolbar-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.settings-dialog-shell {
  height: 100%;
  display: flex;
  justify-content: flex-end;
}

.settings-drawer-card {
  height: 100%;
  overflow-y: auto;
}

.main-footer {
  width: 100%;
  padding: 64px 0 32px !important;
  color: #64748b !important;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc !important;
}

.footer-shell {
  width: min(1280px, calc(100% - 48px));
  margin: 0 auto;
}

.footer-grid {
  display: grid;
  grid-template-columns: 1.25fr repeat(4, minmax(0, 1fr));
  gap: 48px;
  margin-bottom: 48px;
}

.footer-brand-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.footer-brand-row img {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  object-fit: cover;
}

.footer-brand-row strong {
  color: #0f172a;
  font-size: 1.125rem;
}

.footer-brand p {
  margin: 0;
  color: #64748b;
  font-size: 0.875rem;
  line-height: 1.65;
}

.footer-column {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

.footer-column h2 {
  margin: 0 0 4px;
  color: #0f172a;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
}

.footer-column a,
.footer-column button {
  padding: 0;
  color: #64748b;
  border: 0;
  background: transparent;
  font: inherit;
  font-size: 0.875rem;
  line-height: 1.5;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease;
}

.footer-column a:hover,
.footer-column button:hover,
.footer-company a:hover {
  color: #4f46e5;
}

.footer-bottom {
  padding-top: 32px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 32px;
  border-top: 1px solid #e2e8f0;
}

.footer-copyright,
.footer-company p {
  margin: 0;
  color: #94a3b8;
  font-size: 0.75rem;
  line-height: 1.6;
}

.footer-company-wrap {
  display: flex;
  align-items: center;
  gap: 24px;
}

.footer-payment {
  flex: 0 0 auto;
  display: inline-flex;
}

.footer-payment img {
  width: auto;
  height: 40px;
  object-fit: contain;
}

.footer-company a {
  color: inherit;
  text-decoration: underline;
  transition: color 0.2s ease;
}

:global(.v-theme--dark) .main-footer {
  color: #94a3b8 !important;
  border-top-color: #1e293b;
  background: #0f172a !important;
}

:global(.v-theme--dark) .footer-brand-row strong,
:global(.v-theme--dark) .footer-column h2 {
  color: #f8fafc;
}

:global(.v-theme--dark) .footer-brand p,
:global(.v-theme--dark) .footer-column a,
:global(.v-theme--dark) .footer-column button {
  color: #94a3b8;
}

:global(.v-theme--dark) .footer-bottom {
  border-top-color: #1e293b;
}

.custom-snackbar {
  z-index: 9999 !important;
}

@media (max-width: 959px) {
  .footer-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 36px 28px;
  }

  .footer-brand {
    grid-column: 1 / -1;
  }

  .footer-bottom {
    flex-direction: column;
  }
}

@media (max-width: 600px) {
  .main-footer {
    padding: 48px 0 28px !important;
  }

  .footer-shell {
    width: calc(100% - 32px);
  }

  .footer-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .footer-brand {
    grid-column: auto;
  }

  .footer-company-wrap {
    align-items: flex-start;
    flex-direction: column;
  }
}

.custom-ybug-launcher {
  position: fixed;
  bottom: 0;
  right: 100px;
  z-index: 999;
  background-color: #7C4DFF; /* 세련된 보랏빛 */
  color: white;
  padding: 8px 18px;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500; /* 볼드체(700) 대신 500(Medium)을 사용하여 볼드체의 부담을 줄임 */
  transition: background-color 0.2s, transform 0.2s;
  user-select: none;
}

.custom-ybug-launcher:hover {
  background-color: #651FFF;
  transform: translateY(-2px);
}

.launcher-icon {
  margin-right: 6px;
  color: white !important;
  fill: white !important;
  display: flex;
  align-items: center;
}

.launcher-text {
  line-height: 1;
}

@media (max-width: 600px) {
  .custom-ybug-launcher {
    right: 20px;
    padding: 6px 12px;
    font-size: 13px;
  }
}
</style>

<style>
#loading {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
</style>


